import { useMemo } from "react";

import { Box, Drawer, Paper, Stack, Typography } from "@mui/material";
import { FilterBar } from "../../components/FilterBar/FilterBar.component";
import { FilterChipData } from "../../components/FilterBar/filterChips";
import { useCityEscapesDatasource } from "../../hooks/cityEscapes.hooks";
import { useFilters } from "../../providers/FiltersProvider";
import { useSelection } from "../../providers/SelectionProvider";
import { ChallengeDetails, CityEscapeData } from "../../types/cityEscape";
import "./Engimas.page.css";
import { EnigmaDetails } from "./EnigmaDetails";
import { EnigmaEventWeeklyHeatmap } from "./EnigmaEventWeeklyHeatmap";
import { EnigmaHintsDistribution } from "./EnigmaHintsDistributionChart";
import { EnigmaOverview } from "./EnigmaOverview";
import { EnigmaSelector } from "./EnigmaSelector";
import { EnigmaTimelinesHeatMap } from "./EnigmaTimelinesHeatMapChart/EnigmaTimelinesHeatMapChart";
import { EnigmaTrendsOverTimeChart } from "./EnigmaTrendsOverTimeChart/EnigmaTrendsOverTimeChart";
import { EnigmaUserAnswers } from "./EnigmaUserAnswers/EnigmaUserAnswers";
import { EnigmaUsersPaths } from "./EnigmaUsersPathsChart";

export function EnigmasPage() {
  const cityEscapes = useCityEscapesDatasource();
  const selection = useSelection();

  const cityEscape = useMemo(() => {
    if (selection.active.cityEscapeId && cityEscapes.data) {
      return (
        cityEscapes.data.find(
          (ce) => ce.id === selection.active.cityEscapeId
        ) || null
      );
    }

    return null;
  }, [selection.active.cityEscapeId, cityEscapes.data]);

  const enigmaSelection = useMemo(() => {
    if (selection.active.enigmaId && cityEscape) {
      return cityEscape.scenes
        .flatMap((scene) => scene.challenges)
        .find((enigma) => enigma.id === selection.active.enigmaId);
    }
    return null;
  }, [selection.active.enigmaId, cityEscape]);

  const title = useMemo(() => {
    if (cityEscape)
      return `${cityEscape.label} / ${enigmaSelection?.label ?? "--"}`;

    return "Seleziona un'Enigma";
  }, [cityEscape, enigmaSelection]);

  const onCityEscapeSelected = (cityEscape: CityEscapeData | null) => {
    // console.debug("City escape selected", cityEscape);
    // const params = searchParams;
    // if (cityEscape) {
    //   params.set("ce", cityEscape.id);
    // }
    // navigate({ pathname: `/enigmas`, search: params.toString() });
    selection.setActiveSelection({
      cityEscapeId: cityEscape?.id ?? null,
      enigmaId: null,
    });
    // setEnigmaSelection(null);
  };

  const onEnigmaSelected = (enigma: ChallengeDetails | null) => {
    // console.debug("Enigma selected", enigma);
    // const params = searchParams;
    // if (enigma) {
    //   params.set("enigma", enigma.id);
    // }
    // navigate({ pathname: `/enigmas`, search: params.toString() });
    selection.setActiveSelection({ enigmaId: enigma?.id ?? null });

    // setEnigmaSelection(enigma);
  };

  const filters = useFilters();

  const onRemoveFilter = (chipData: FilterChipData) => {
    console.debug("Remove filter", chipData);
    filters.update({ [chipData.key]: undefined });
  };

  return (
    < >
      <Drawer id="enigmas-drawer" variant="permanent" anchor="right">
        <EnigmaSelector
          cityEscapes={cityEscapes.data}
          isLoading={cityEscapes.isLoading}
          selectedEscapeId={selection.active.cityEscapeId}
          selectedEnigmaId={selection.active.enigmaId}
          onCityEscapeChange={onCityEscapeSelected}
          onEnigmaChange={onEnigmaSelected}
        />
      </Drawer>
      <Stack direction={"column"} spacing={2} id="container" maxHeight={"100%"} overflow="auto">
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <FilterBar
          sticky={{ top: 0 }}
          filters={filters.active}
          onClearAll={filters.clear}
          onReset={filters.reset}
          onEdit={filters.update}
        />
        <Paper elevation={0} sx={{ padding: 2 }}>
          <Stack direction="column" spacing={2}>
            {enigmaSelection && (
              <Stack direction={"row"} spacing={2}>
                <EnigmaDetails enigma={enigmaSelection} />
                <EnigmaUserAnswers
                  cityEscapeId={selection.active.cityEscapeId}
                  enigmaId={enigmaSelection?.data?.id}
                />
              </Stack>
            )}
            <EnigmaOverview
              cityEscapeId={selection.active.cityEscapeId}
              enigmaId={enigmaSelection?.data?.id}
              filters={filters.active}
            />

            <EnigmaUsersPaths
              cityEscapeId={selection.active.cityEscapeId}
              enigmaId={enigmaSelection?.data?.id}
              filters={filters.active}
            />

            <Stack direction="row" spacing={2}>
              <Box flex={1}>
                <EnigmaHintsDistribution
                  cityEscapeId={selection.active.cityEscapeId}
                  enigmaId={enigmaSelection?.data?.id}
                  filters={filters.active}
                />
              </Box>
              <Box flex={1}>
                <EnigmaTimelinesHeatMap
                  cityEscapeId={selection.active.cityEscapeId}
                  enigmaId={enigmaSelection?.data?.id}
                  filters={filters.active}
                  totalHints={5}
                />
              </Box>
            </Stack>

            <EnigmaTrendsOverTimeChart
              cityEscapeId={selection.active.cityEscapeId}
              enigmaId={enigmaSelection?.data?.id}
              filters={filters.active}
            />

            <EnigmaEventWeeklyHeatmap
              cityEscapeId={selection.active.cityEscapeId}
              enigmaId={enigmaSelection?.data?.id}
              filters={filters.active}
            />
          </Stack>
        </Paper>
      </Stack>
    </>
  );
}


export default EnigmasPage;